import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CrawlStatus {
  domain: string;
  requestedDomain?: string;
  canonicalDomain?: string;
  aliasApplied?: boolean;
  totalUrls: number;
  pagesCrawled: number;
  pagesFailed: number;
  pagesNoindex?: number;
  pagesInQueue: number;
  pagesAnalyzed?: number;
  isComplete: boolean;
  progress: number; // percentage
  phase?: 'crawling' | 'analyzing' | 'complete' | 'idle';
  estimatedTimeRemaining?: number; // seconds (optional)
  crawlCost?: number;
  crawlMode?: 'sitemap' | 'curated' | 'domain';
  durationMs?: number;
  coverage?: {
    expected: number;
    analyzed: number;
    missingUrls: string[];
    /**
     * Classified by the backend (`assessCrawlCoverage`, SS001-10) so the threshold has
     * one owner. `gap` is normal sitemap attrition; `severe` means the analyzed set is
     * a fraction of the site and nothing built on it should be trusted blindly.
     * Absent on crawls that completed before the field shipped.
     */
    severity?: 'ok' | 'gap' | 'severe';
  };
  /** Set when sitemap preflight fails or a sitemap crawl severely under-delivers. */
  errorMessage?: string;
}

export interface LocaleGroup {
  key: string;
  label: string;
  hreflangs: string[];
  urlCount: number;
  confidence: 'hreflang' | 'heuristic';
  exampleUrls: string[];
}

export interface StartCrawlOptions {
  limit?: number;
  priority?: 'low' | 'medium' | 'high';
  frequency?: 'daily' | 'weekly' | 'monthly' | 'manual';
  enableBrowserRendering?: boolean;
  selectedSitemaps?: string[];
  selectedLocales?: string[];
  minLastmodFrom?: string | null;
  perSitemapLimits?: Record<string, number>;
  perSitemapLimitMode?: 'recent' | 'oldest' | 'balanced';
  uploadedSitemaps?: Array<{ filename: string; content: string }>;
  /** Default sitemap (single task + hosted allowlist). Use curated for per-URL fallback. */
  crawlMode?: 'sitemap' | 'curated' | 'domain';
  /** FW-ONB-01 overrides. Omitting both applies the suggested excludes. */
  keepNeverTrackKeys?: string[];
  excludeNeverTrackKeys?: string[];
}

export interface SitemapTreeNode {
  sitemapUrl: string;
  type: 'sitemapindex' | 'urlset';
  urlCount: number;
  filteredUrlCount?: number;
  childSitemapCount: number;
  exampleUrls: string[];
  lastmod?: string;
  /** Dominant locale group key when leaf sitemaps are split by language. */
  dominantLocaleKey?: string;
  children?: SitemapTreeNode[];
}

export interface SitemapParseFailure {
  sitemapUrl: string;
  error: string;
}

export interface StartCrawlResponse {
  success: boolean;
  urlsAdded: number;
  tasksCreated: number;
  crawlMode?: 'sitemap' | 'curated' | 'domain';
  requestedDomain?: string;
  canonicalDomain?: string;
  aliasApplied?: boolean;
  message?: string;
}

export type ProcessingStatus = 'no_data' | 'not_started' | 'partial' | 'complete';

export interface LinksBackfillStatus {
  status: ProcessingStatus;
  totalPages: number;
  pagesWithLinks: number;
  lastFetchedAt: string | null;
}

export interface LinksClassifyStatus {
  status: ProcessingStatus;
  pagesWithLinks: number;
  pagesWithClassifiedLinks: number;
  lastClassifiedAt: string | null;
}

export interface TopicsMapStatus {
  status: ProcessingStatus;
  mappingCount: number;
  lastUpdatedAt: string | null;
  /**
   * Topics map is workspace-global. This is true when the last map run predates
   * this domain's crawl, i.e. it must be re-run to incorporate this domain.
   */
  staleForDomain?: boolean;
}

export interface TopicsAggregateStatus {
  status: ProcessingStatus;
  aggregateCount: number;
  pagesWithTopicSignals: number;
  lastUpdatedAt: string | null;
}

export interface CrawlUrlPreviewEntry {
  url: string;
  sourceSitemapUrl: string;
  priority: number;
  /** Set when the row was dropped by a never-track rule rather than by the cap. */
  excludedReason?: string;
}

export interface CrawlUrlPreview {
  included: CrawlUrlPreviewEntry[];
  excluded: CrawlUrlPreviewEntry[];
}

/**
 * FW-ONB-01 (SS001-19): a low-SEO-value folder or page found in the sitemap
 * before the crawl. Mirrors `backend/src/utils/never-track-paths.ts`.
 */
export interface NeverTrackGroup {
  key: string;
  kind: 'folder' | 'page';
  path: string;
  categoryId: string;
  categoryLabel: string;
  reason: string;
  urlCount: number;
  exampleUrls: string[];
  /** False when the group is too big a share of the site to drop without a click. */
  defaultExcluded: boolean;
}

export interface CrawlEstimate {
  requestedDomain?: string;
  canonicalDomain?: string;
  aliasApplied?: boolean;
  sitemapUrl: string;
  discoveredSitemaps?: string[];
  sitemapTree?: SitemapTreeNode[];
  sitemapParseFailures?: SitemapParseFailure[];
  totalPagesInSitemap: number;
  duplicateUrlsInSitemaps?: number;
  parsedSitemaps?: number;
  sitemapParseTruncated?: boolean;
  filteredPages: number;
  selectedPagesInSitemap?: number;
  selectedFilteredPages?: number;
  neverTrackGroups?: NeverTrackGroup[];
  neverTrackExcludedCount?: number;
  minLastmodFrom?: string | null;
  localeGroups?: LocaleGroup[];
  localeDetectionConfidence?: 'hreflang' | 'heuristic' | null;
  localePartitionedSitemaps?: boolean;
  selectedLocales?: string[];
  limit: number;
  estimatedCostPerPage: number;
  estimatedTotalCost: number;
  selectedEstimatedTotalCost?: number;
  urlsToCrawl?: string[];
  crawlUrlPreview?: CrawlUrlPreview;
  sitemapSource?: 'remote' | 'upload';
}

export interface FailedPage {
  url: string;
  errorMessage: string;
  category: 'noindex' | 'failure';
  displayLabel: string;
  taskId: string;
  createdAt: string;
  completedAt: string | null;
}

export interface EstEnrichmentStatus {
  status: ProcessingStatus;
  /** Analyzed pages (PageAnalysis count) — denominator for enrichment progress */
  totalPages: number;
  pagesWithTraffic?: number;
  pagesWithKeywords?: number;
  /** All tracked Url rows for the domain (may exceed analyzed pages on legacy crawls) */
  trackedUrls?: number;
  lastFetchedAt: string | null;
}

export interface PageDemandStatus {
  status: ProcessingStatus;
  totalPages: number;
  labeledUrls: number;
  trackedUrls?: number;
  lastComputedAt: string | null;
}

export interface UrlSerpStatus {
  status: ProcessingStatus;
  totalPages: number;
  urlsWithSnapshots: number;
  trackedUrls?: number;
  lastFetchedAt: string | null;
  /** FW-URL-14 answer-structure LLM cache coverage (warmed during URL SERP). */
  answerStructure?: {
    uniqueKeywords: number;
    cachedKeywords: number;
    urlsWithSerp: number;
    urlsWithCachedStructure: number;
  };
}

export interface InternalLinkSuggestionsStatus {
  status: ProcessingStatus | 'blocked';
  suggestionCount: number;
  lastGeneratedAt: string | null;
  blockedReason?: string | null;
  prerequisitesReady?: boolean;
  pagesWithClassifiedLinks?: number;
  pagesWithExtractedText?: number;
  pagesWithTopicSignals?: number;
}

export interface SupportingPagesBuildStatus {
  status: ProcessingStatus | 'blocked';
  snapshotCount: number;
  totalUrls: number;
  processedUrls: number;
  progressPercent: number;
  estimatedRemainingMs: number | null;
  estimatedRemainingLabel: string | null;
  lastGeneratedAt: string | null;
  startedAt: string | null;
  blockedReason?: string | null;
  prerequisitesReady?: boolean;
  /** True when the marked-competitor set changed since this snapshot was built. */
  staleForCompetitorSet?: boolean;
  supportingPagesReady?: boolean;
  contentGapAvailable?: boolean;
}

/** Readiness of a single marked competitor to contribute to supporting-pages builds. */
/** Why a host was kept out of the Setup focal-domain picker. */
export type SetupFocalExclusionReason =
  | 'competitor'
  | 'subdomain_of_listed_domain'
  | 'support_subdomain';

export interface SetupFocalDomains {
  domains: string[];
  hidden: Array<{ domain: string; reason: SetupFocalExclusionReason }>;
}

export interface CompetitorReadiness {
  domain: string;
  crawled: boolean;
  /** Crawl-record truth, shared with the Prep list chip (SS001-8). */
  crawlState?: 'none' | 'crawling' | 'analyzing' | 'complete' | 'failed';
  pagesAnalyzed: number;
  /** Pages that captured extractable body text. 0 with pagesAnalyzed > 0 ⇒ content not captured. */
  pagesWithContent?: number;
  hasTopicSignals: boolean;
  aggregateCount: number;
  ready: boolean;
  activeJob: 'queued' | 'running' | null;
}

export interface CompetitorStatus {
  focalDomain: string;
  competitors: CompetitorReadiness[];
  staleForCompetitorSet: boolean;
}

/** A competitor domain suggested from the focal domain's tracked-URL SERP snapshots. */
export interface SerpCompetitorSuggestion {
  domain: string;
  /** How many of the focal domain's tracked URLs this domain ranks for. */
  appearsOnUrlCount: number;
  /** Distinct ranking page URLs collected across those SERPs. */
  urlCount: number;
  pageUrls: string[];
  avgPosition: number | null;
  sampleQueries: string[];
}

export interface SerpCompetitorSuggestionsResponse {
  focalDomain: string;
  minTrackedUrls: number;
  snapshotsConsidered: number;
  suggestions: SerpCompetitorSuggestion[];
}

/** One tracked URL's best-ranked rival page (the "Top SERP rival" for that page). */
export interface TopSerpRivalPick {
  focalUrlId: string;
  focalUrl: string;
  rivalUrl: string;
  rivalDomain: string;
  rivalPosition: number;
  yourPosition: number | null;
  /** The tracked URL's SERP query (its demand claim) — the reason this rival matters. */
  query: string;
  serpFetchedAt: string | null;
}

export interface TopSerpRivalsEstimate {
  scopeDomain: string;
  /** The picks that will run — already limited to the top-N ceiling. */
  picks: TopSerpRivalPick[];
  /** N — focal pages this run covers. */
  trackedUrlsWithRival: number;
  /** M — focal pages with a rival ahead at all. Rendered as "N of M". */
  candidateFocals: number;
  /** The ceiling in force; `null` when the environment turned it off. */
  ceiling: number | null;
  capped: boolean;
  /** M − N — deferred to on-demand refresh or a later run, not dropped. */
  deferredFocals: number;

  uniqueRivalCount: number;
  alreadyAnalyzedCount: number;
  crawlsNeeded: number;
  estimatedCostPerPage: number;
  estimatedCrawlCost: number;

  /** Upper bound on the chained Labs fetch: unique rivals without keywords yet. */
  keywordFetchesNeeded: number;
  alreadyHaveKeywordsCount: number;
  estimatedCostPerKeywordUrl: number;
  estimatedKeywordsCost: number;

  /** What the single confirm authorizes: both legs of the chain. */
  estimatedTotalCost: number;
}

export interface TopSerpRivalFailure {
  focalUrl: string;
  rivalUrl: string;
  rivalDomain: string;
  query: string;
}

export interface TopSerpRivalsStatus {
  status: 'not_started' | 'building' | 'ready' | 'failed';
  phase: 'crawling' | 'evaluating' | null;
  totalRivals: number;
  totalUrls: number;
  crawledRivals: number;
  evaluatedUrls: number;
  failedRivals: number;
  progressPercent: number;
  estimatedRemainingMs: number | null;
  estimatedRemainingLabel: string | null;
  failures: TopSerpRivalFailure[];
  serviceAvailable: boolean;
  startedAt: string | null;
  generatedAt: string | null;
  lastError: string | null;
  /** The "N of M" this build ran under. */
  ceiling: number | null;
  candidateFocals: number;
  capped: boolean;
  deferredFocals: number;
  /** Whether the confirm that started this run also bought the keyword leg. */
  chainKeywords: boolean;
  /** SS001-7 actuals — populated once the build reaches `ready`. */
  actualCostUsd: number;
  crawlsBilled: number;
  durationMs: number | null;
}

export interface SerpRivalKeywordFailure {
  url: string;
  error: string;
}

export interface SerpRivalKeywordsEstimate {
  scopeDomain: string;
  uniqueRivalCount: number;
  alreadyHaveKeywordsCount: number;
  needsFetchCount: number;
  noAnalysisCount: number;
  estimatedCostPerUrl: number;
  estimatedCost: number;
}

export interface SerpRivalKeywordsStatus {
  status: 'not_started' | 'building' | 'ready' | 'failed';
  totalUrls: number;
  fetchedUrls: number;
  skippedUrls: number;
  failedUrls: number;
  progressPercent: number;
  estimatedRemainingMs: number | null;
  estimatedRemainingLabel: string | null;
  failures: SerpRivalKeywordFailure[];
  startedAt: string | null;
  generatedAt: string | null;
  lastError: string | null;
  /** SS001-7 actuals — populated once the fetch reaches `ready`. */
  actualCostUsd: number;
  durationMs: number | null;
  /** True when this leg ran as the chained tail of a rivals build. */
  chained: boolean;
}

/** A processing job currently queued or running (for live checklist state). */
export type ProcessingJobType =
  | 'links-backfill'
  | 'links-classify'
  | 'topics-map'
  | 'topics-aggregate'
  | 'action-plan-internal-links'
  | 'supporting-pages-by-topic'
  | 'supporting-pages-opportunities';

export interface ActiveProcessingJob {
  type: ProcessingJobType;
  domain: string;
  status: 'queued' | 'running';
  startedAt: string | null;
  queuedAt: string | null;
}

export interface DomainProcessingStatus {
  domain: string;
  linksBackfill: LinksBackfillStatus;
  linksClassify: LinksClassifyStatus;
  topicsMap: TopicsMapStatus;
  topicsAggregate: TopicsAggregateStatus;
  estTraffic?: EstEnrichmentStatus;
  estKeywords?: EstEnrichmentStatus;
  pageDemand?: PageDemandStatus;
  urlSerp?: UrlSerpStatus;
  internalLinkSuggestions?: InternalLinkSuggestionsStatus;
  supportingPagesByTopic?: SupportingPagesBuildStatus;
  supportingPagesOpportunities?: SupportingPagesBuildStatus;
  /** Alias domains whose DataForSEO data feeds into this domain's enrichment (e.g. pre-rebrand domains) */
  enrichmentAliasDomains?: string[];
  /** Jobs currently queued/running for this domain (plus workspace-global topics-map). */
  activeJobs?: ActiveProcessingJob[];
}

export interface DeleteDomainDataResponse {
  success: boolean;
  data: {
    domain: string;
    deleted: {
      pageAnalysis: number;
      tasks: number;
      urls: number;
      domainCrawls: number;
      domainTopicAggregates: number;
      rankedKeywords: number;
      competitorFinderRuns: number;
      relatedPagesCaches: number;
      pageComparisonExecutions: number;
      topicFilteredEntityDiscoveries: number;
      topicSharedDomainCoverages: number;
      topicSharedEntityLists: number;
      topicDomainCoverages: number;
      shareLinks: number;
      domainAliases?: number;
    };
  };
  message?: string;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class CrawlService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * Domains for Crawl Management (sitemap/curated crawls + substantial inventory).
   * Excludes Top SERP rival one-off hosts unless they have a DomainCrawl record.
   */
  getCrawlManagedDomains(): Observable<{ success: boolean; data: string[] }> {
    return this.http.get<{ success: boolean; data: string[] }>(
      `${this.API_URL}/urls/domains`,
      { params: { managed: '1' } }
    );
  }

  /**
   * Every host with at least one Url row (includes rival one-offs).
   */
  getDomains(): Observable<{ success: boolean; data: string[] }> {
    return this.http.get<{ success: boolean; data: string[] }>(`${this.API_URL}/urls/domains`);
  }

  /**
   * Domains that can be the FOCAL domain of a Setup run: crawl-managed hosts
   * minus marked competitors and support subdomains (SS001-13 / SS001-14).
   * `hidden` carries the excluded hosts + reason so the UI can still show them.
   */
  getSetupFocalDomains(keepDomains: string[] = []): Observable<{ success: boolean; data: SetupFocalDomains }> {
    const keep = keepDomains.map((d) => (d || '').trim()).filter(Boolean).join(',');
    return this.http.get<{ success: boolean; data: SetupFocalDomains }>(
      `${this.API_URL}/urls/domains/setup-focal`,
      keep ? { params: { keep } } : {}
    );
  }

  /**
   * Start a crawl for a domain
   */
  startCrawl(domain: string, options: StartCrawlOptions = {}): Observable<StartCrawlResponse> {
    return this.http.post<StartCrawlResponse>(`${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/start`, {
      limit: options.limit || 750,
      priority: options.priority || 'high',
      frequency: options.frequency || 'manual',
      enableBrowserRendering: options.enableBrowserRendering || false,
      selectedSitemaps: options.selectedSitemaps || [],
      selectedLocales: options.selectedLocales || [],
      minLastmodFrom: options.minLastmodFrom || null,
      perSitemapLimits: options.perSitemapLimits || {},
      perSitemapLimitMode: options.perSitemapLimitMode || 'recent',
      uploadedSitemaps: options.uploadedSitemaps || [],
      crawlMode: options.crawlMode || 'sitemap',
      keepNeverTrackKeys: options.keepNeverTrackKeys || [],
      excludeNeverTrackKeys: options.excludeNeverTrackKeys || []
    });
  }

  private buildEstimateQueryParams(
    options: {
      limit?: number;
      selectedSitemaps?: string[];
      selectedLocales?: string[];
      minLastmodFrom?: string | null;
      perSitemapLimits?: Record<string, number>;
      perSitemapLimitMode?: 'recent' | 'oldest' | 'balanced';
      keepNeverTrackKeys?: string[];
      excludeNeverTrackKeys?: string[];
      includeUrls?: boolean;
      skipInspect?: boolean;
    } = {}
  ): string {
    const search = new URLSearchParams();
    if (options.limit) search.set('limit', String(options.limit));
    if ((options.selectedSitemaps || []).length > 0) {
      search.set('selectedSitemaps', (options.selectedSitemaps || []).join(','));
    }
    if ((options.selectedLocales || []).length > 0) {
      search.set('selectedLocales', (options.selectedLocales || []).join(','));
    }
    if (options.minLastmodFrom) {
      search.set('minLastmodFrom', options.minLastmodFrom);
    }
    if (options.perSitemapLimits && Object.keys(options.perSitemapLimits).length > 0) {
      search.set('perSitemapLimits', JSON.stringify(options.perSitemapLimits));
    }
    if (options.perSitemapLimitMode) {
      search.set('perSitemapLimitMode', options.perSitemapLimitMode);
    }
    // JSON, not the comma-joined convention above: a never-track group key is a
    // URL path, and a path may legally contain a comma.
    if ((options.keepNeverTrackKeys || []).length > 0) {
      search.set('keepNeverTrackKeys', JSON.stringify(options.keepNeverTrackKeys));
    }
    if ((options.excludeNeverTrackKeys || []).length > 0) {
      search.set('excludeNeverTrackKeys', JSON.stringify(options.excludeNeverTrackKeys));
    }
    if (options.includeUrls) {
      search.set('includeUrls', '1');
    }
    if (options.skipInspect) {
      search.set('skipInspect', '1');
    }
    return search.toString();
  }

  /**
   * Estimate crawl cost for a domain (parses sitemap, zero API cost)
   */
  estimateCrawl(
    domain: string,
    options: {
      limit?: number;
      selectedSitemaps?: string[];
      selectedLocales?: string[];
      minLastmodFrom?: string | null;
      perSitemapLimits?: Record<string, number>;
      perSitemapLimitMode?: 'recent' | 'oldest' | 'balanced';
      keepNeverTrackKeys?: string[];
      excludeNeverTrackKeys?: string[];
      includeUrls?: boolean;
      skipInspect?: boolean;
    } = {}
  ): Observable<{ success: boolean; data: CrawlEstimate }> {
    const params = this.buildEstimateQueryParams(options);
    return this.http.get<{ success: boolean; data: CrawlEstimate }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/estimate${params ? `?${params}` : ''}`
    );
  }

  /**
   * Estimate crawl cost from operator-uploaded sitemap XML files (bot-protected sites).
   */
  estimateCrawlFromUpload(
    domain: string,
    files: File[],
    options: {
      limit?: number;
      selectedSitemaps?: string[];
      selectedLocales?: string[];
      minLastmodFrom?: string | null;
      perSitemapLimits?: Record<string, number>;
      perSitemapLimitMode?: 'recent' | 'oldest' | 'balanced';
      keepNeverTrackKeys?: string[];
      excludeNeverTrackKeys?: string[];
      includeUrls?: boolean;
      skipInspect?: boolean;
    } = {}
  ): Observable<{ success: boolean; data: CrawlEstimate }> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('sitemaps', file, file.name);
    }
    const params = this.buildEstimateQueryParams(options);
    return this.http.post<{ success: boolean; data: CrawlEstimate }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/estimate-upload${params ? `?${params}` : ''}`,
      formData
    );
  }

  /**
   * Get failed crawl pages for a domain
   */
  getFailedPages(domain: string): Observable<FailedPage[]> {
    return this.http.get<{ success: boolean; data: FailedPage[] }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/failed`
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Failed to get failed pages');
      })
    );
  }

  /**
   * Get crawl status for a domain
   */
  getCrawlStatus(domain: string): Observable<CrawlStatus> {
    return this.http.get<{ success: boolean; data: CrawlStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/status`
    ).pipe(
      // Extract data from response
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error('Failed to get crawl status');
        }
      })
    );
  }

  /**
   * Get processing status for a domain (links/topics workflows)
   */
  getProcessingStatus(domain: string): Observable<DomainProcessingStatus> {
    return this.http.get<{ success: boolean; data: DomainProcessingStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/processing-status`
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error('Failed to get processing status');
        }
      })
    );
  }

  /**
   * Trigger a processing step for a domain
   */
  runProcessingStep(domain: string, step: 'links-backfill' | 'links-classify' | 'topics-map' | 'topics-aggregate'): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/${step}`, {}
    );
  }

  /**
   * Run all processing steps sequentially for a domain
   */
  runAllProcessing(
    domain: string,
    topicsMapDomains?: string[]
  ): Observable<{ success: boolean; message: string }> {
    const body = topicsMapDomains && topicsMapDomains.length ? { topicsMapDomains } : {};
    return this.http.post<{ success: boolean; message: string }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/run-all`, body
    );
  }

  /** Run a single processing endpoint by its `process/<endpoint>` suffix. */
  runProcessEndpoint(domain: string, endpoint: string): Observable<{ success: boolean; message?: string; data?: any }> {
    return this.http.post<{ success: boolean; message?: string; data?: any }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/${endpoint}`, {}
    );
  }

  /** Build supporting pages by topic (cheap topic-diagnostic snapshot). */
  runSupportingPagesByTopic(domain: string) {
    return this.runProcessEndpoint(domain, 'supporting-pages-by-topic');
  }

  /** Build supporting page opportunities (content-gap sidecar; opt-in, slow). */
  runSupportingPagesOpportunities(domain: string) {
    return this.runProcessEndpoint(domain, 'supporting-pages-opportunities');
  }

  /** Refresh estimated organic traffic (Labs). */
  refreshTraffic(domain: string) {
    return this.runProcessEndpoint(domain, 'traffic-enrichment');
  }

  /** Refresh ranked keywords domain dump (Labs cost; opt-in). */
  refreshKeywords(domain: string) {
    return this.runProcessEndpoint(domain, 'ranked-keywords-enrichment');
  }

  /** Recompute page demand labels (claim vs realization) from stored analyses. */
  refreshPageDemand(domain: string) {
    return this.runProcessEndpoint(domain, 'page-demand-labeling');
  }

  /** Rebuild internal-link suggestions. */
  refreshInternalLinks(domain: string) {
    return this.runProcessEndpoint(domain, 'internal-link-suggestions');
  }

  /** Run minimal topic prep (topics-map + topics-aggregate) for one competitor domain. */
  runCompetitorPrep(competitorDomain: string, focalDomain?: string) {
    const body = focalDomain ? { focalDomain } : {};
    return this.http.post<{ success: boolean; message?: string; data?: any }>(
      `${this.API_URL}/domains/${encodeURIComponent(competitorDomain)}/process/competitor-prep`,
      body
    );
  }

  /** Run topic prep for all of the focal domain's ready (crawled) competitors at once. */
  runCompetitorPrepAll(focalDomain: string): Observable<{ success: boolean; message?: string; data?: { jobIds: string[]; readyDomains: string[] } }> {
    return this.http.post<{ success: boolean; message?: string; data?: { jobIds: string[]; readyDomains: string[] } }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/competitors/prep-all`, {}
    );
  }

  /** Per-competitor readiness + supporting-pages staleness for the focal domain. */
  getCompetitorStatus(focalDomain: string): Observable<{ success: boolean; data: CompetitorStatus }> {
    return this.http.get<{ success: boolean; data: CompetitorStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/competitors/status`
    );
  }

  /**
   * Competitor domains suggested from the focal domain's tracked-URL SERP snapshots
   * (domains that recur across multiple of your pages' SERPs).
   */
  getSerpCompetitorSuggestions(
    focalDomain: string
  ): Observable<{ success: boolean; data: SerpCompetitorSuggestionsResponse }> {
    return this.http.get<{ success: boolean; data: SerpCompetitorSuggestionsResponse }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/competitors/serp-suggestions`
    );
  }

  /** Preview the "Top SERP rivals" build: which rival page per tracked URL, count + cost. */
  getTopSerpRivalsPreview(
    focalDomain: string
  ): Observable<{ success: boolean; data: TopSerpRivalsEstimate }> {
    return this.http.get<{ success: boolean; data: TopSerpRivalsEstimate }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/serp-rivals/preview`
    );
  }

  /**
   * Start the (detached) chained build behind the one confirm: crawl rivals,
   * precompute section coverage, then fetch their ranked keywords.
   * `chainKeywords: false` runs the crawl leg alone.
   */
  runTopSerpRivals(
    focalDomain: string,
    options: { chainKeywords?: boolean } = {}
  ): Observable<{ success: boolean; data: { started: boolean; alreadyRunning: boolean; status: TopSerpRivalsStatus } }> {
    return this.http.post<{ success: boolean; data: { started: boolean; alreadyRunning: boolean; status: TopSerpRivalsStatus } }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/serp-rivals/run`,
      { chainKeywords: options.chainKeywords !== false }
    );
  }

  /** Poll Top SERP rivals build progress. */
  getTopSerpRivalsStatus(
    focalDomain: string
  ): Observable<{ success: boolean; data: TopSerpRivalsStatus }> {
    return this.http.get<{ success: boolean; data: TopSerpRivalsStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/serp-rivals/status`
    );
  }

  /** Preview Labs ranked-keyword fetches for unique Top SERP rival pages. */
  getSerpRivalKeywordsPreview(
    focalDomain: string
  ): Observable<{ success: boolean; data: SerpRivalKeywordsEstimate }> {
    return this.http.get<{ success: boolean; data: SerpRivalKeywordsEstimate }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/serp-rivals/keywords/preview`
    );
  }

  runSerpRivalKeywords(
    focalDomain: string
  ): Observable<{ success: boolean; data: { started: boolean; alreadyRunning: boolean; status: SerpRivalKeywordsStatus } }> {
    return this.http.post<{ success: boolean; data: { started: boolean; alreadyRunning: boolean; status: SerpRivalKeywordsStatus } }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/serp-rivals/keywords/run`,
      {}
    );
  }

  getSerpRivalKeywordsStatus(
    focalDomain: string
  ): Observable<{ success: boolean; data: SerpRivalKeywordsStatus }> {
    return this.http.get<{ success: boolean; data: SerpRivalKeywordsStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/serp-rivals/keywords/status`
    );
  }

  /**
   * Delete all known domain-related data (destructive)
   */
  deleteDomainData(domain: string, confirmDomain: string): Observable<DeleteDomainDataResponse> {
    return this.http.post<DeleteDomainDataResponse>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/delete-data`,
      { confirmDomain }
    );
  }
}

